(() => {
  // laurel-globe-s26/sources/src/timeline.js
  var clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  var ease = (value) => {
    const t = clamp(value);
    return t * t * (3 - 2 * t);
  };
  var mix = (a, b, t) => a + (b - a) * t;
  function makeTimeline(config, destinations) {
    let cursor = config.introHold;
    const stops = destinations.map((destination, index) => {
      const start = cursor;
      const arrival = start + config.travel;
      const end = arrival + destination.hold;
      cursor = end;
      return { index, start, arrival, end, destination };
    });
    return { stops, introEnd: config.introHold, total: cursor + config.outro, config };
  }
  function sampleTimeline(timeline, distance) {
    const { config, stops } = timeline;
    const d = clamp(distance, 0, timeline.total);
    const first = stops[0];
    if (!first || d < timeline.introEnd) return {
      index: -1,
      phase: "intro",
      opacity: 1,
      introOpacity: 1,
      yaw: config.startYaw,
      pitch: config.startPitch,
      handoff: 0
    };
    for (const stop of stops) {
      if (d > stop.end) continue;
      const previous = stops[stop.index - 1]?.destination;
      const t = clamp((d - stop.start) / (stop.arrival - stop.start));
      const rotation = ease(t);
      const atStop = d >= stop.arrival;
      const outgoing = !atStop && t < 0.22;
      const index = outgoing ? stop.index - 1 : stop.index;
      return {
        index,
        phase: atStop ? "hold" : "travel",
        opacity: atStop ? 1 : outgoing ? 1 - ease(t / 0.22) : ease((t - 0.78) / 0.22),
        introOpacity: stop.index === 0 ? 1 - ease(t / 0.25) : 0,
        yaw: mix(previous?.yaw ?? config.startYaw, stop.destination.yaw, rotation),
        pitch: mix(previous?.pitch ?? config.startPitch, stop.destination.pitch, rotation),
        handoff: stop.index === 0 ? rotation : 1
      };
    }
    const last = stops.at(-1);
    return {
      index: last.index,
      phase: "outro",
      opacity: 1,
      introOpacity: 0,
      yaw: last.destination.yaw,
      pitch: last.destination.pitch,
      handoff: 1
    };
  }

  // laurel-globe-s26/sources/src/controller.js
  var WorldGlobe = class {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.config = JSON.parse(root.querySelector("[data-tg-config]").textContent);
      this.config.model = root.dataset.model || "";
      this.cards = [...root.querySelectorAll("[data-tg-card]")];
      this.icons = [...root.querySelectorAll("[data-tg-icon]")];
      this.pins = [...root.querySelectorAll("[data-tg-pin]")];
      this.links = this.cards.map((_, index) => root.querySelector('[data-tg-index="' + index + '"]'));
      this.stage = root.querySelector("[data-tg-stage]");
      this.host = root.querySelector("[data-tg-canvas]");
      this.laurel = root.querySelector("[data-tg-laurel]");
      this.intro = root.querySelector("[data-tg-intro]");
      this.timeline = makeTimeline(this.config, this.config.destinations);
      this.motion = matchMedia("(prefers-reduced-motion: reduce)");
      this.small = matchMedia("(max-width: 749px)");
      this.frame = 0;
      this.visible = false;
      this.near = false;
      this.lastIndex = -2;
      this.request = this.request.bind(this);
      this.measure = this.measure.bind(this);
      this.onScroll = () => {
        if (!this.enhanced) return;
        this.request();
      };
      window.addEventListener("scroll", this.onScroll, { passive: true, signal: this.signal });
      document.addEventListener("scroll", this.onScroll, { passive: true, capture: true, signal: this.signal });
      window.addEventListener("resize", this.measure, { passive: true, signal: this.signal });
      this.motion.addEventListener("change", this.measure, { signal: this.signal });
      this.small.addEventListener("change", this.measure, { signal: this.signal });
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) this.cancelFrame();
        else {
          this.request();
          this.ensureGlobe();
        }
      }, { signal: this.signal });
      root.addEventListener("focusout", () => queueMicrotask(this.request), { signal: this.signal });
      const header = document.getElementById("header-component");
      this.header = header;
      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(this.measure);
        this.resizeObserver.observe(this.stage);
        if (header) this.resizeObserver.observe(header);
      }
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          if (this.visible) {
            this.request();
            this.ensureGlobe();
          } else this.cancelFrame();
        });
        this.observer.observe(root);
        this.preloadObserver = new IntersectionObserver((entries) => {
          this.near = entries[0].isIntersecting;
          if (this.near) {
            this.hydrate(this.root.querySelector("[data-tg-laurel-frame]"));
            this.ensureGlobe();
          }
        }, { rootMargin: "250px 0px" });
        this.preloadObserver.observe(root);
      } else {
        this.visible = true;
        this.near = true;
      }
      this.measure();
    }
    hydrate(container) {
      container?.querySelectorAll("img[data-src]").forEach((img) => {
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          delete img.dataset.srcset;
        }
        img.src = img.dataset.src;
        delete img.dataset.src;
      });
    }
    measure() {
      if (this.signal.aborted) return;
      const old = this.enhanced;
      let top = 0;
      if (this.header) {
        const style = getComputedStyle(this.header);
        if (["fixed", "sticky"].includes(style.position) || this.header.hasAttribute("sticky")) top = this.header.offsetHeight;
      }
      const offset = Math.max(0, top + (Number(this.config.headerGap) || 0));
      const availableHeight = Math.max(0, window.innerHeight - offset);
      this.root.style.setProperty("--tg-top", offset + "px");
      const minimumHeight = this.small.matches ? 620 : 380;
      const reason = this.motion.matches ? "reduced-motion" : !this.cards.length ? "no-destinations" : availableHeight < minimumHeight ? "short-viewport" : "";
      this.enhanced = !reason;
      this.root.dataset.scrollMode = reason || "enabled";
      this.root.classList.toggle("tg26--compact", this.enhanced && !this.small.matches && availableHeight < 660);
      this.root.classList.toggle("tg26--scroll", this.enhanced);
      this.root.classList.add("tg26--ready");
      this.unit = Math.max(1, this.stage.clientHeight);
      if (this.enhanced) this.root.style.setProperty("--tg-length", (this.timeline.total + 1) * this.unit + "px");
      else this.root.style.removeProperty("--tg-length");
      if (!this.enhanced) {
        this.cards.forEach((card) => {
          card.inert = false;
          card.removeAttribute("aria-hidden");
          this.hydrate(card);
        });
        this.intro.inert = false;
        this.intro.removeAttribute("aria-hidden");
        this.hydrate(this.root.querySelector("[data-tg-laurel-frame]"));
      }
      if (old !== this.enhanced) this.lastIndex = -2;
      this.motionDistance = null;
      this.motionTime = null;
      this.needsResize = true;
      this.request();
      this.ensureGlobe();
    }
    async ensureGlobe() {
      if (!this.near && !this.visible || document.hidden || this.motion.matches || this.loading || this.globe || this.failed || this.signal.aborted) return;
      if (!this.config.model && !this.config.texture) return;
      this.loading = true;
      this.root.dataset.modelState = "loading";
      try {
        const module = await import(this.config.renderer);
        if (this.signal.aborted) return;
        const globe = await module.createGlobe(this.host, this.config, this.signal);
        if (this.signal.aborted) {
          globe.dispose();
          return;
        }
        this.globe = globe;
        globe.canvas.addEventListener("webglcontextlost", (event) => {
          event.preventDefault();
          this.failed = true;
          this.root.classList.remove("tg26--3d");
          this.root.dataset.modelState = "error";
          globe.dispose();
          this.globe = null;
          this.pins.forEach((pin) => {
            pin.hidden = true;
          });
        }, { signal: this.signal });
        this.root.dataset.modelState = "ready";
        this.needsResize = true;
        this.request();
      } catch (error) {
        if (!this.signal.aborted) {
          this.failed = true;
          this.root.dataset.modelState = "error";
          console.warn("[TOTOKA World Globe] The selected 3D asset could not load; navigation remains available.", error);
        }
      } finally {
        this.loading = false;
      }
    }
    cancelFrame() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.motionDistance = null;
      this.motionTime = null;
    }
    request() {
      if (this.frame || !this.visible || document.hidden || this.signal.aborted) return;
      this.frame = requestAnimationFrame((time) => {
        this.frame = 0;
        this.paint(time);
      });
    }
    paint(time) {
      if (!this.visible || document.hidden || this.signal.aborted) return;
      if (this.needsResize && this.globe) {
        this.globe.resize();
        this.needsResize = false;
        this.lastAngles = null;
      }
      const top = parseFloat(this.root.style.getPropertyValue("--tg-top")) || 0;
      let targetDistance = clamp((-this.root.getBoundingClientRect().top + top) / this.unit, 0, this.timeline.total);
      let immediate = !this.enhanced;
      if (this.editorIndex != null) {
        const stop = this.timeline.stops[this.editorIndex];
        if (stop) {
          targetDistance = (stop.arrival + stop.end) / 2;
          immediate = true;
        }
      }
      const focusedIndex = this.cards.findIndex((card) => card.contains(document.activeElement));
      if (this.enhanced && focusedIndex >= 0) {
        const stop = this.timeline.stops[focusedIndex];
        targetDistance = (stop.arrival + stop.end) / 2;
        immediate = true;
      }
      const dt = this.motionTime == null ? 1e3 / 60 : clamp(time - this.motionTime, 0, 64);
      this.motionTime = time;
      if (immediate || this.motionDistance == null || Math.abs(targetDistance - this.motionDistance) > 1.5) {
        this.motionDistance = targetDistance;
      } else {
        this.motionDistance += (targetDistance - this.motionDistance) * (1 - Math.exp(-dt / 220));
      }
      const settling = !immediate && Math.abs(targetDistance - this.motionDistance) > 0.5 / this.unit;
      if (!settling) {
        this.motionDistance = targetDistance;
        this.motionTime = null;
      }
      const distance = this.motionDistance;
      let state = sampleTimeline(this.timeline, distance);
      if (!this.enhanced) state = { ...state, yaw: this.config.startYaw, pitch: this.config.startPitch, index: -1, handoff: 0, introOpacity: 1 };
      this.root.dataset.phase = state.phase;
      this.root.style.setProperty("--tg-intro-alpha", state.introOpacity);
      this.root.style.setProperty("--tg-copy-alpha", state.opacity);
      this.root.style.setProperty("--tg-copy-shift", (1 - state.opacity) * 12 + "px");
      const mobile = this.small.matches;
      const start = mobile ? this.config.laurelMobileStart : this.config.laurelStart;
      const end = mobile ? this.config.laurelMobileEnd : this.config.laurelEnd;
      this.laurel.style.left = mix(start[0], end[0], state.handoff) + "%";
      this.laurel.style.top = mix(start[1], end[1], state.handoff) + Math.sin(state.handoff * Math.PI) * (mobile ? 8 : 17) + "%";
      this.laurel.style.setProperty("--tg-laurel-scale", 1 - Math.sin(state.handoff * Math.PI) * 0.23);
      const introHidden = this.enhanced && state.introOpacity < 0.15;
      this.intro.inert = introHidden;
      if (introHidden) this.intro.setAttribute("aria-hidden", "true");
      else this.intro.removeAttribute("aria-hidden");
      this.cards.forEach((card, index) => {
        const active = index === state.index;
        card.classList.toggle("is-active", active);
        const hidden = this.enhanced && (!active || state.opacity < 0.15);
        card.inert = hidden;
        if (hidden) card.setAttribute("aria-hidden", "true");
        else card.removeAttribute("aria-hidden");
        this.icons[index]?.classList.toggle("is-active", active);
        if (active) this.links[index]?.setAttribute("aria-current", "true");
        else this.links[index]?.removeAttribute("aria-current");
      });
      if (this.enhanced && state.index !== this.lastIndex) {
        if (state.index >= 0) {
          this.hydrate(this.cards[state.index]);
          this.hydrate(this.icons[state.index]);
        }
        this.lastIndex = state.index;
      }
      if (this.enhanced) {
        const next = this.timeline.stops.find((stop) => stop.arrival >= distance && stop.start - distance < 0.4);
        if (next) {
          this.hydrate(this.cards[next.index]);
          this.hydrate(this.icons[next.index]);
        }
      }
      if (this.globe) {
        if (!this.lastAngles || this.lastAngles[0] !== state.yaw || this.lastAngles[1] !== state.pitch) {
          this.projected = this.globe.draw(state.yaw, state.pitch);
          this.lastAngles = [state.yaw, state.pitch];
        }
        const pins = this.projected;
        this.root.classList.add("tg26--3d");
        pins.forEach((point, index) => {
          const pin = this.pins[index];
          if (!pin) return;
          pin.hidden = !this.enhanced || index !== state.index || !point.visible || state.opacity < 0.15;
          pin.style.left = point.x + "%";
          pin.style.top = point.y + "%";
          pin.style.opacity = state.opacity;
        });
      }
      if (settling) this.request();
    }
    selectBlock(id) {
      this.editorIndex = this.cards.findIndex((card) => card.dataset.blockId === id);
      this.request();
    }
    destroy() {
      this.cancelFrame();
      this.abort.abort();
      this.observer?.disconnect();
      this.preloadObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.globe?.dispose();
      this.root.classList.remove("tg26--scroll", "tg26--compact", "tg26--ready", "tg26--3d");
      delete this.root.dataset.scrollMode;
      this.root.style.removeProperty("--tg-length");
      this.cards.forEach((card) => {
        card.inert = false;
        card.removeAttribute("aria-hidden");
      });
    }
  };
  function mountHero(root) {
    const video = root.querySelector("video");
    const stage = root.querySelector("[data-tgh-stage]");
    const copy = root.querySelector("[data-tgh-copy]");
    const sectionWrapper = root.closest(".shopify-section");
    const nextSection = sectionWrapper?.nextElementSibling || null;
    const nextWorld = nextSection?.querySelector?.("[data-totoka-globe-s26]") || null;
    const abort = new AbortController(), signal = abort.signal;
    const motion = matchMedia("(prefers-reduced-motion: reduce)");
    const mobile = matchMedia("(max-width: 749px)");
    let visible = false, frame = 0, current = null, lastTime = null;
    let enabled = false, top = 0, span = 1;
    const variables = ["--tgh-media-scale", "--tgh-media-alpha", "--tgh-copy-alpha", "--tgh-copy-shift", "--tgh-handoff-alpha"];
    const setWorldAlpha = (value) => {
      if (nextWorld) nextWorld.style.setProperty("--tg-handoff-alpha", String(clamp(value)));
    };
    const cancel = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      current = null;
      lastTime = null;
    };
    const resetCopy = () => {
      if (copy) {
        copy.inert = false;
        copy.removeAttribute("aria-hidden");
      }
    };
    const updateVideo = () => {
      if (!video) return;
      const mobilePoster = mobile.matches && root.dataset.mobileImage === "true";
      if (visible && !document.hidden && !motion.matches && !mobilePoster) {
        video.querySelectorAll("source[data-src]").forEach((source) => {
          source.src = source.dataset.src;
          delete source.dataset.src;
        });
        if (!video.dataset.loaded) {
          video.dataset.loaded = "true";
          video.load();
        }
        video.play().then(() => {
          if (!visible || document.hidden || motion.matches || mobile.matches && root.dataset.mobileImage === "true") video.pause();
        }).catch(() => {
        });
      } else video.pause();
    };
    const paint = (time) => {
      frame = 0;
      if (!enabled || !visible || document.hidden || signal.aborted) return;
      const focused = copy?.contains(document.activeElement);
      const target = focused ? 0 : clamp((top - root.getBoundingClientRect().top) / span);
      const dt = lastTime == null ? 1e3 / 60 : clamp(time - lastTime, 0, 64);
      lastTime = time;
      if (current == null || focused || Math.abs(target - current) > 0.7) current = target;
      else current += (target - current) * (1 - Math.exp(-dt / 180));
      const settling = Math.abs(target - current) > 1e-3;
      if (!settling) {
        current = target;
        lastTime = null;
      }
      const handoff = ease((current - 0.38) / 0.62);
      const alpha = 1 - ease((current - 0.08) / 0.48);
      const mediaFade = ease((current - 0.28) / 0.72);
      root.style.setProperty("--tgh-media-scale", 1.025 - current * 0.025);
      root.style.setProperty("--tgh-media-alpha", 1 - mediaFade * 0.62);
      root.style.setProperty("--tgh-copy-alpha", alpha);
      root.style.setProperty("--tgh-copy-shift", -34 * ease(current) + "px");
      root.style.setProperty("--tgh-handoff-alpha", 0.18 + handoff * 0.82);
      setWorldAlpha(handoff);
      if (copy) {
        copy.inert = alpha < 0.15;
        if (copy.inert) copy.setAttribute("aria-hidden", "true");
        else copy.removeAttribute("aria-hidden");
      }
      if (settling) request();
    };
    const request = () => {
      if (enabled && visible && !document.hidden && !signal.aborted && !frame) frame = requestAnimationFrame(paint);
    };
    const measure = () => {
      cancel();
      const header = document.getElementById("header-component");
      top = header && (["fixed", "sticky"].includes(getComputedStyle(header).position) || header.hasAttribute("sticky")) ? header.offsetHeight : 0;
      enabled = !!stage && root.dataset.passage === "true" && !motion.matches && window.innerHeight - top >= (mobile.matches ? 520 : 380);
      root.style.setProperty("--tgh-top", top + "px");
      root.classList.toggle("tgh26--passage", enabled);
      if (enabled) {
        const height = Math.max(1, stage.clientHeight);
        const requestedLength = Number(root.dataset.passageLength) || 32;
        const run = height * clamp(Math.min(requestedLength, 32) / 100, 0.22, 0.32);
        root.style.setProperty("--tgh-length", height + run + "px");
        span = run;
        if (nextWorld && nextSection) {
          nextSection.classList.add("totoka-hero-handoff-section");
          setWorldAlpha(0);
        }
      } else {
        root.style.removeProperty("--tgh-length");
        variables.forEach((name) => root.style.removeProperty(name));
        resetCopy();
        nextSection?.classList.remove("totoka-hero-handoff-section");
        setWorldAlpha(1);
      }
      updateVideo();
      request();
    };
    const observer = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      if (!visible) {
        cancel();
        if (root.getBoundingClientRect().bottom <= 0) setWorldAlpha(1);
      }
      updateVideo();
      request();
    });
    observer.observe(stage || root);
    window.addEventListener("scroll", request, { passive: true, signal });
    document.addEventListener("scroll", request, { passive: true, capture: true, signal });
    window.addEventListener("resize", measure, { passive: true, signal });
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) cancel();
      updateVideo();
      request();
    }, { signal });
    root.addEventListener("focusin", request, { signal });
    root.addEventListener("focusout", () => queueMicrotask(request), { signal });
    motion.addEventListener("change", measure, { signal });
    mobile.addEventListener("change", measure, { signal });
    let resizeObserver;
    if (stage && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(stage);
      const header = document.getElementById("header-component");
      if (header) resizeObserver.observe(header);
    }
    measure();
    return () => {
      abort.abort();
      cancel();
      observer.disconnect();
      resizeObserver?.disconnect();
      video?.pause();
      root.classList.remove("tgh26--passage");
      root.style.removeProperty("--tgh-length");
      variables.forEach((name) => root.style.removeProperty(name));
      nextSection?.classList.remove("totoka-hero-handoff-section");
      nextWorld?.style.removeProperty("--tg-handoff-alpha");
      resetCopy();
    };
  }
  if (!window.TotokaWorldGlobeS26) {
    let mount = function(scope = document) {
      scope.querySelectorAll("[data-totoka-globe-s26]").forEach((root) => {
        if (!instances.has(root)) try {
          instances.set(root, new WorldGlobe(root));
        } catch (error) {
          console.warn("[TOTOKA World Globe]", error);
        }
      });
      scope.querySelectorAll("[data-totoka-globe-hero-s26]").forEach((root) => {
        if (!heroes.has(root)) heroes.set(root, mountHero(root));
      });
    };
    const instances = /* @__PURE__ */ new Map(), heroes = /* @__PURE__ */ new Map();
    window.TotokaWorldGlobeS26 = { mount };
    document.addEventListener("shopify:section:load", (event) => mount(event.target));
    document.addEventListener("shopify:section:unload", (event) => {
      for (const [root, instance] of instances) if (event.target.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
      for (const [root, destroy] of heroes) if (event.target.contains(root)) {
        destroy();
        heroes.delete(root);
      }
    });
    document.addEventListener("shopify:block:select", (event) => {
      for (const [root, instance] of instances) if (root.contains(event.target)) instance.selectBlock(event.detail.blockId);
    });
    document.addEventListener("shopify:block:deselect", () => {
      for (const instance of instances.values()) {
        instance.editorIndex = null;
        instance.request();
      }
    });
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => mount(), { once: true });
    else mount();
  }
})();
